import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSiteData } from "@/hooks/useSite";
import { LineReveal, Reveal, ParallaxImage, FadeIn } from "@/components/Motion";
import MethodSteps from "@/components/MethodSteps";
import Stamp from "@/components/Stamp";

export default function Metodo() {
  const { settings: seoSettings } = useSiteData();
  const seo = seoSettings.seo?.["il-metodo"] || {};
  useSEO({
    title: seo.title || "Il metodo — Il percorso nutrizionale K Nutrition | Salerno",
    description:
      seo.description ||
      "Il metodo K Nutrition in quattro passi: ascolto, valutazione, personalizzazione e monitoraggio. Un percorso nutrizionale che parte da te, con Dott.ssa Francesca Notarfrancesco a Salerno.",
    path: "/il-metodo",
  });

  const { settings } = useSiteData();
  const statement = settings.content.method.statement;

  return (
    <>
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden" data-testid="metodo-header">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <FadeIn>
              <p className="flex items-center gap-3 text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium">
                <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
                Il metodo K Nutrition
              </p>
            </FadeIn>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6">
              <LineReveal delay={0.2}>Il percorso</LineReveal>
              <LineReveal delay={0.4} className="italic text-deeptaupe">
                parte da te.
              </LineReveal>
            </h1>
            <FadeIn delay={0.6}>
              <p className="text-base lg:text-lg leading-relaxed text-charcoal/75 mt-8 max-w-xl">{statement}</p>
            </FadeIn>
            <FadeIn delay={0.75}>
              <Link
                to="/servizi"
                data-testid="metodo-services-link"
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-deeptaupe text-ivory text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-charcoal"
              >
                Scopri i servizi
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeIn>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <FadeIn delay={0.3}>
              <div className="relative max-w-[460px] mx-auto">
                <div aria-hidden="true" className="absolute -top-6 -right-6 w-44 h-44 rounded-full bg-peach -z-10" />
                <div className="overflow-hidden rounded-[28px] aspect-[4/5]">
                  <ParallaxImage
                    src={settings.photos.method}
                    alt="Dott.ssa Francesca Notarfrancesco solleva un broccoli come un bilanciere, piena di energia"
                    className="w-full h-full"
                    priority
                  />
                </div>
                <Stamp
                  className="absolute -bottom-8 -left-8 lg:-left-12 w-24 h-24 lg:w-32 lg:h-32 z-10"
                />
                <p className="mt-6 lg:mt-8 text-[13px] text-charcoal/60 text-center">
                  Ogni percorso comincia da piccole scelte quotidiane.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28 bg-sand/20" data-testid="metodo-steps">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <MethodSteps steps={settings.content.method.steps} />
        </div>
      </section>
    </>
  );
}
